import type {PropertyDefinition, PropertyValue, PropertyValuesRecord} from '@github-ui/custom-properties-types'
import {isEmptyPropertyValue} from '@github-ui/custom-properties-types/helpers'
import {ListItem, ListView} from '@github-ui/list-view'
import {FilterIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, Box, Button, Link, Text, TextInput, Token} from '@primer/react'
import {Blankslate} from '@primer/react/drafts'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useState} from 'react'

const propertyColumnResponsiveStyle: BetterSystemStyleObject = {
  width: [100, 100, 200],
}
interface Props {
  definitions: PropertyDefinition[]
  values: PropertyValuesRecord
  onEditClick?: () => void
}
export function RepoPropertiesList({definitions, values, onEditClick}: Props) {
  const [filterTerm, setFilterTerm] = useState('')

  if (definitions.length === 0) {
    return (
      <Blankslate border>
        <Blankslate.Heading>No custom properties set for this repository.</Blankslate.Heading>
        <Blankslate.Description>
          <Link
            inline
            href="https://docs.github.com/enterprise-cloud@latest/organizations/managing-organization-settings/managing-custom-properties-for-repositories-in-your-organization"
          >
            Learn more about custom properties
          </Link>{' '}
          and how to set them at the organization level.
        </Blankslate.Description>
      </Blankslate>
    )
  }

  const filteredDefinitions = definitions.filter(getFilterPredicate(filterTerm))

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <TextInput
        block
        value={filterTerm}
        onChange={e => setFilterTerm(e.target.value)}
        leadingVisual={FilterIcon}
        aria-label="Filter"
        placeholder="Filter properties"
      />

      <Box data-hpc sx={{border: '1px solid', borderColor: 'border.muted', borderRadius: 2}}>
        {filteredDefinitions.length ? (
          <ListView
            title="Custom properties"
            variant="compact"
            metadata={
              <ListView.Metadata
                title={
                  <Text sx={{color: 'fg.muted'}}>
                    {filteredDefinitions.length} {filteredDefinitions.length === 1 ? 'property' : 'properties'}
                  </Text>
                }
                actionsLabel="Actions"
                actions={[
                  {
                    key: 'edit',
                    render(isOverflowMenu) {
                      if (!onEditClick) {
                        return <></>
                      }
                      return isOverflowMenu ? (
                        <ActionList.Item onClick={onEditClick}>
                          <ActionList.LeadingVisual>
                            <PencilIcon />
                          </ActionList.LeadingVisual>
                          Edit
                        </ActionList.Item>
                      ) : (
                        <Button leadingVisual={PencilIcon} onClick={onEditClick}>
                          Edit
                        </Button>
                      )
                    },
                  },
                ]}
              />
            }
          >
            {filteredDefinitions.map(definition => (
              <ListItem
                key={definition.propertyName}
                title={
                  <ListItem.Title
                    containerSx={{
                      ...propertyColumnResponsiveStyle,
                      px: 2,
                    }}
                    headerSx={{fontWeight: 600, wordWrap: 'break-word', whiteSpace: 'unset', display: 'block'}}
                    value={definition.propertyName}
                  />
                }
              >
                <ListItem.MainContent>
                  <Box sx={{height: '100%', pt: 2, maxWidth: '100%', wordWrap: 'break-word'}}>
                    <ValueCell definition={definition} value={values[definition.propertyName]} />
                  </Box>
                </ListItem.MainContent>
              </ListItem>
            ))}
          </ListView>
        ) : (
          <Blankslate border>
            <Blankslate.Heading>No properties that match</Blankslate.Heading>
          </Blankslate>
        )}
      </Box>
    </Box>
  )
}

function ValueCell({definition, value}: {definition: PropertyDefinition; value?: PropertyValue}) {
  const {valueType} = definition
  const displayValue = value || definition.defaultValue || undefined
  if (isEmptyPropertyValue(displayValue)) {
    return <Text sx={{color: 'fg.muted'}}>No value set</Text>
  }

  switch (valueType) {
    case 'string':
      return <>{displayValue}</>
    case 'multi_select':
      return (
        <>
          {(displayValue as string[]).map(v => (
            <Token sx={{mr: 1}} key={v} data-testid="single-select-cell" text={v} />
          ))}
        </>
      )
    case 'single_select':
    case 'boolean':
      return <Token data-testid="single-select-cell" text={displayValue} />
  }
}

function getFilterPredicate(term: string) {
  return (item: PropertyDefinition) => item.propertyName.toLowerCase().includes(term.toLowerCase())
}

try{ RepoPropertiesList.displayName ||= 'RepoPropertiesList' } catch {}
try{ ValueCell.displayName ||= 'ValueCell' } catch {}